// MobileLogin — tela de LOGIN mobile (parede A1). Email + senha (teclado nativo)
// + "Continuar com Google" + links pra criar conta e recuperar senha.
//
// Touch-first: inputs com type/autocomplete corretos (teclado e gerenciador de
// senhas nativos), alvos >= 48px, sem foco de controle remoto (nada de Norigin).
// A lógica de auth vem do MOTOR (account-auth) — esta tela só apresenta.

import { useTranslation } from '@app-iptv/i18n'
import { useState } from 'react'

import { loginWithEmail, type AuthErrorCode } from '../auth/account-auth'

import { authErrorKey, googleErrorKey } from './auth-messages'
import { GoogleButton } from './GoogleButton'
import styles from './MobileAuth.module.css'
import { MobileBrandLockup } from './MobileBrandLockup'

interface MobileLoginProps {
  /** Login (email ou Google) deu certo — sessão Swipe já persistida. */
  onAuthenticated: () => void
  /** Trocar pra tela de cadastro. */
  onGoToSignup: () => void
  /** Trocar pra tela de recuperação de senha. */
  onGoToForgot: () => void
}

/** Validação local mínima (o backend é a verdade). E-mail vazio/sem @ ou senha
 *  vazia → bloqueia o submit sem ir ao servidor. */
function looksLikeEmail(value: string): boolean {
  return /.+@.+\..+/.test(value.trim())
}

export function MobileLogin({
  onAuthenticated,
  onGoToSignup,
  onGoToForgot,
}: MobileLoginProps): JSX.Element {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [fieldError, setFieldError] = useState<'email' | 'password' | null>(null)

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (busy) return
    setErrorKey(null)
    setFieldError(null)

    if (!looksLikeEmail(email)) {
      setFieldError('email')
      setErrorKey('mobileAuth.error.invalidEmail')
      return
    }
    if (password.length < 1) {
      setFieldError('password')
      setErrorKey('mobileAuth.error.passwordRequired')
      return
    }

    setBusy(true)
    try {
      const result = await loginWithEmail(email, password)
      if (result.ok) onAuthenticated()
      else setErrorKey(authErrorKey(result.error))
    } finally {
      setBusy(false)
    }
  }

  const onAuthError = (code: AuthErrorCode): void => setErrorKey(authErrorKey(code))
  const onGoogleError = (code: 'load_failed' | 'unavailable'): void =>
    setErrorKey(googleErrorKey(code))

  return (
    <div className={styles.screen}>
      <div className={styles.content}>
        <header className={styles.header}>
          <span className={styles.brandLogo}>
            <MobileBrandLockup height={48} />
          </span>
          <h1 className={styles.title}>{t('mobileAuth.login.title')}</h1>
          <p className={styles.subtitle}>{t('mobileAuth.login.subtitle')}</p>
        </header>

        <form className={styles.form} onSubmit={(e) => void handleSubmit(e)} noValidate>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="mobile-login-email">
              {t('mobileAuth.emailLabel')}
            </label>
            <input
              id="mobile-login-email"
              className={`${styles.input} ${fieldError === 'email' ? styles.inputError : ''}`}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder={t('mobileAuth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="mobile-login-password">
              {t('mobileAuth.passwordLabel')}
            </label>
            <input
              id="mobile-login-password"
              className={`${styles.input} ${fieldError === 'password' ? styles.inputError : ''}`}
              type="password"
              autoComplete="current-password"
              placeholder={t('mobileAuth.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
          </div>

          {errorKey && (
            <p className={styles.error} role="alert">
              {t(errorKey)}
            </p>
          )}

          <button type="submit" className={`${styles.button} ${styles.primary}`} disabled={busy}>
            {busy ? <span className={styles.spinner} aria-hidden="true" /> : t('mobileAuth.login.submit')}
          </button>
        </form>

        <div className={styles.divider}>{t('mobileAuth.or')}</div>

        <GoogleButton
          busy={busy}
          onBusyChange={setBusy}
          onSuccess={onAuthenticated}
          onAuthError={onAuthError}
          onGoogleError={onGoogleError}
        />

        <div className={styles.links}>
          <div className={styles.linkRow}>
            {t('mobileAuth.login.noAccount')}{' '}
            <button type="button" className={styles.linkButton} onClick={onGoToSignup} disabled={busy}>
              {t('mobileAuth.login.createAccount')}
            </button>
          </div>
          <button type="button" className={styles.linkButton} onClick={onGoToForgot} disabled={busy}>
            {t('mobileAuth.login.forgotPassword')}
          </button>
        </div>
      </div>
    </div>
  )
}
